
import { Component, Inject, Vue } from 'vue-property-decorator';
import ActionBar from '@/ui/ActionBar.vue';
import PageEditor from '@/ui/PageEditor.vue';
import {
    MESSAGE_DURATION_DEFAULT,
    PAGE_DEFAULT_FONT_SIZE,
    PAGE_DEFAULT_FONT_TYPE,
    PAGE_DEFAULT_LAYOUT,
    PAGE_DEFAULT_ORIENTATION,
    PAGE_DEFAULT_PAPER_TYPE,
} from '@/container';
import PageSelector from '@/ui/PageSelector.vue';
import SelectionPane from '@/ui/SelectionPane.vue';
import PageRenderPortal from '@/ui/PageRenderPortal.vue';
import { BeforeDestroyAware, CreatedAware } from '@/util/lifecycle';
import PageActionBar from '@/ui/PageActionBar.vue';
import Image from '@/api/mjegb/Image';
import { layoutOptions } from '@/util/book/layouts';
import ConfirmDialog from '@/ui/ConfirmDialog.vue';
import SaveDialog from '@/ui/SaveDialog.vue';
import BookRepository from '@/api/BookRepository';
import Book from '@/api/mjegb/Book';
import Page from '@/api/mjegb/Page';
import { Subscription } from 'rxjs';
import GlobalState from '@/util/GlobalState';
import OpenDialog from '@/ui/OpenDialog.vue';
import { fontOptions } from '@/util/book/fonts';

export interface TextConfiguration {
    text: string;
    centered: boolean;
}

interface PageConfiguration {
    id: number;
    layout: string;
    fontType: string;
    fontSize: number;
    imageCellCache: { [key: string]: Image };
    textCellCache: TextConfiguration;
}

interface BookConfiguration {
    paperType: string;
    orientation: string;
    pages: PageConfiguration[];
}

let pageCounter = 0;

function parseFontType(fontType: string): string {
    const fontTypeLower = fontType.toLowerCase();
    const validIds = fontOptions.map((t) => t.id);
    return validIds.includes(fontTypeLower) ? fontTypeLower : PAGE_DEFAULT_FONT_TYPE;
}

function createPage(): PageConfiguration {
    pageCounter++;
    return {
        id: pageCounter,
        layout: PAGE_DEFAULT_LAYOUT,
        fontType: PAGE_DEFAULT_FONT_TYPE,
        fontSize: PAGE_DEFAULT_FONT_SIZE,
        imageCellCache: {},
        textCellCache: {
            text: '',
            centered: false,
        },
    };
}

function createPageFrom(source: Page): PageConfiguration {
    pageCounter++;
    return {
        id: pageCounter,
        layout: source.layout,
        fontType: parseFontType(source.fontType),
        fontSize: source.fontSize,
        imageCellCache: source.images.reduce((cache, image) => {
            const key = image.row + 'x' + image.column;
            cache[key] = image;
            return cache;
        }, {} as { [key: string]: Image }),
        textCellCache: {
            text: source.text,
            centered: source.textCentered,
        },
    };
}

function createBook(empty = false): BookConfiguration {
    return {
        paperType: PAGE_DEFAULT_PAPER_TYPE,
        orientation: PAGE_DEFAULT_ORIENTATION,
        pages: empty ? [] : [createPage()],
    };
}

@Component({
    components: {
        OpenDialog,
        SaveDialog,
        ConfirmDialog,
        PageActionBar,
        PageRenderPortal,
        SelectionPane,
        PageSelector,
        ActionBar,
        PageEditor,
    },
})
export default class BookBuilder extends Vue implements CreatedAware, BeforeDestroyAware {
    public selectedPage = 0;
    public showPreviewCounter = 0;
    public generatePdfCounter = 0;
    public loading = false;
    public bookId: number | null = null;
    public name: string | null = null;
    public organization: number | null = null;
    public heading = '';
    public bookConfiguration: BookConfiguration = createBook();
    public layoutOptions = layoutOptions;
    public showConfirmDialog = false;
    public confirmDialogBody = '';
    public onConfirm = () => {};
    public showOpenDialog = false;
    public showSaveDialog = false;

    public get selectedPageConfiguration(): PageConfiguration {
        return this.bookConfiguration.pages[this.selectedPage];
    }

    @Inject() private readonly globalState!: GlobalState;
    @Inject() private readonly bookRepository!: BookRepository;
    private bookSubscription = new Subscription();

    public created(): void {
        this.heading = this.$t('editor.heading').toString();
    }

    public beforeDestroy(): void {
        this.bookSubscription.unsubscribe();
    }

    public onBookConfigurationChanged(changes: Partial<BookConfiguration>): void {
        this.bookConfiguration = { ...this.bookConfiguration, ...changes };
        if ('orientation' in changes || 'paperType' in changes) {
            this.bookConfiguration.pages.forEach((page) => {
                const orientation = this.bookConfiguration.orientation;
                const paperType = this.bookConfiguration.paperType;
                const layout = this.layoutOptions.find((t) => t.type === page.layout);
                if (
                    !layout?.allowedOrientations.includes(orientation) ||
                    !layout?.allowedPageTypes.includes(paperType)
                ) {
                    page.layout =
                        this.layoutOptions.find(
                            (t) =>
                                t.allowedOrientations.includes(orientation) && t.allowedPageTypes.includes(paperType),
                        )?.type || 't';
                }
            });
        }
    }

    public onPageConfigurationChanged(changes: Partial<PageConfiguration>): void {
        if ('layout' in changes) {
            this.selectedPageConfiguration.layout = changes.layout!;
        }
        if ('fontType' in changes) {
            this.selectedPageConfiguration.fontType = changes.fontType!;
        }
        if ('fontSize' in changes) {
            this.selectedPageConfiguration.fontSize = changes.fontSize!;
        }
    }

    public onPageImageCellCacheUpdate(index: number, { key, value }: { key: string; value: Image | null }): void {
        if (value !== null) {
            this.bookConfiguration.pages[index].imageCellCache[key] = value;
        } else if (key in this.bookConfiguration.pages[index].imageCellCache) {
            delete this.bookConfiguration.pages[index].imageCellCache[key];
        }
    }

    public onPageTextCellCacheUpdate(index: number, config: TextConfiguration): void {
        this.bookConfiguration.pages[index].textCellCache = {
            text: config.text,
            centered: config.centered,
        };
    }

    public onNewBook(confirmed: boolean): void {
        if (confirmed) {
            this.bookId = null;
            this.name = null;
            this.organization = null;
            this.heading = this.$t('editor.heading').toString();
            this.selectedPage = 0;
            this.bookConfiguration = createBook();
        } else {
            this.confirmDialogBody = this.$t('actions.onNew.confirm').toString();
            this.onConfirm = () => this.onNewBook(true);
            this.showConfirmDialog = true;
        }
    }

    public onShowPreview(): void {
        this.showPreviewCounter++;
    }

    public onGeneratePdf(): void {
        this.generatePdfCounter++;
    }

    public onHeadingChanged(heading: string): void {
        this.heading = heading;
    }

    public onSelectPage(index: number): void {
        this.selectedPage = index;
    }

    public onAddPage(): void {
        this.bookConfiguration.pages.push(createPage());
        this.selectedPage = this.bookConfiguration.pages.length - 1;
    }

    public onDeletePage(): void {
        if (this.bookConfiguration.pages.length > 1) {
            this.bookConfiguration.pages.splice(this.selectedPage, 1);
            if (this.selectedPage > 0) {
                this.selectedPage--;
            }
        }
    }

    public onShowOpenBookDialog(): void {
        this.showOpenDialog = true;
    }

    public onOpenBook(id: number): void {
        this.bookSubscription.unsubscribe();
        this.bookSubscription = this.bookRepository.findById(id).subscribe((resource) => {
            this.loading = resource.isLoading;
            if (resource.hasData()) {
                const book: Book = resource.data;
                const configuration = createBook(true);
                configuration.paperType = book.paperType.toLowerCase();
                configuration.orientation = book.orientation.toLowerCase();
                configuration.pages = book.pages.map((t) => createPageFrom(t));
                this.bookId = book.id;
                this.name = book.name;
                this.heading = book.heading;
                this.organization = book.organisationId;
                this.bookConfiguration = configuration;
                this.globalState.emitOnMessageBus({
                    text: this.$t('actions.onOpen.success').toString(),
                    duration: MESSAGE_DURATION_DEFAULT,
                });
            }
            if (resource.isError) {
                this.globalState.emitOnMessageBus({
                    text: this.$t('actions.onOpen.error').toString(),
                    error: true,
                });
            }
        });
    }

    public onSave(saveAs: boolean) {
        if (this.name === null || this.name.length === 0 || saveAs) {
            this.showSaveDialog = true;
        } else {
            const cellSpots = [0, 1, 2];
            const book: Book = {
                id: this.bookId,
                name: this.name,
                heading: this.heading,
                userId: null, // Server will fill this in as necessary
                organisationId: this.organization,
                paperType: this.bookConfiguration.paperType,
                orientation: this.bookConfiguration.orientation,
                pages: this.bookConfiguration.pages.map(
                    (page): Page => ({
                        layout: page.layout,
                        fontType: page.fontType,
                        fontSize: page.fontSize,
                        images: cellSpots
                            .flatMap((r) => cellSpots.map((c) => r + 'x' + c))
                            .filter((key) => key in page.imageCellCache)
                            .map((key) => page.imageCellCache[key]),
                        text: page.textCellCache.text,
                        textCentered: page.textCellCache.centered,
                    }),
                ),
            };
            this.bookSubscription.unsubscribe();
            this.bookSubscription = this.bookRepository.save(book).subscribe((resource) => {
                this.loading = resource.isLoading;
                if (resource.isSuccess) {
                    if (resource.hasData()) {
                        this.bookId = resource.data.id;
                    }
                    this.globalState.emitOnMessageBus({
                        text: this.$t('actions.onSave.success').toString(),
                        duration: MESSAGE_DURATION_DEFAULT,
                    });
                } else if (resource.isError) {
                    this.globalState.emitOnMessageBus({
                        text: this.$t('actions.onSave.error').toString(),
                        error: true,
                    });
                }
            });
        }
    }

    public onSaveAs({ name, organization }: { name: string; organization: number | null }): void {
        this.showSaveDialog = false;
        this.bookId = null;
        this.name = name;
        this.organization = organization;
        this.onSave(false);
    }

    public onDeleteBook(confirmed: boolean): void {
        if (this.bookId !== null) {
            if (confirmed) {
                this.bookSubscription.unsubscribe();
                this.bookSubscription = this.bookRepository.delete(this.bookId).subscribe((resource) => {
                    this.loading = resource.isLoading;
                    if (resource.isSuccess) {
                        this.onNewBook(true);
                        this.globalState.emitOnMessageBus({
                            text: this.$t('actions.onDelete.success').toString(),
                            duration: MESSAGE_DURATION_DEFAULT,
                        });
                    } else if (resource.isError) {
                        this.globalState.emitOnMessageBus({
                            text: this.$t('actions.onDelete.error').toString(),
                            error: true,
                        });
                    }
                });
            } else {
                this.confirmDialogBody = this.$t('actions.onDelete.confirm').toString();
                this.onConfirm = () => this.onDeleteBook(true);
                this.showConfirmDialog = true;
            }
        }
    }
}
